/**
 * Ephemeral cross-route handoff for "use this asset as source" actions.
 *
 * The Asset Gallery's preview modal pushes an asset here, then navigates
 * to `/labs/image-lab/{studioId}`. ImageLab's mount effect consumes
 * (reads + clears) the asset and seeds it into the canvas as if the user
 * had uploaded it.
 *
 * Single-slot intentionally — there's no use case for queueing multiple
 * sources, and a queue would require a UX for picking among them. If
 * a second handoff lands before the first is consumed, the newer one
 * wins (last write wins; the route the user is heading to gets the most
 * recent intent).
 *
 * Not persisted — refreshing the page drops the pending source, which
 * is the right default for an ephemeral nav-state handoff.
 */

import { create } from 'zustand';
import type { AssetSummary } from '../domain';

interface SourceHandoffState {
  pending: AssetSummary | null;
  setHandoff: (asset: AssetSummary) => void;
  consumeHandoff: () => AssetSummary | null;
  clearHandoff: () => void;
}

export const useSourceHandoffStore = create<SourceHandoffState>((set, get) => ({
  pending: null,
  setHandoff: (asset: AssetSummary) => set({ pending: asset }),
  consumeHandoff: () => {
    const pending = get().pending;
    if (pending !== null) set({ pending: null });
    return pending;
  },
  clearHandoff: () => set({ pending: null }),
}));

/**
 * Adapt an AssetSummary into the ResultImageInfo shape the ImageLab
 * canvas's `images` state holds. Mirrors `uploadResponseToResultImage`
 * — same target shape, different source. The asset already has a
 * server-side id (`assetId`), so `sourceFromImage` will produce a
 * `{sourceImageId}` ref for the next operation — the BE can resolve
 * it via `generated_assets` lookup without re-uploading.
 */
import type { ResultImageInfo } from '../domain';

export function assetSummaryToResultImage(asset: AssetSummary): ResultImageInfo {
  return {
    id: asset.assetId,
    url: asset.url,
    thumbnailUrl: asset.thumbnailUrl,
    // 2026-05-26 — thread the HMAC-signed companions through so the next
    // Konva / new Image() load doesn't 401 against the unsigned endpoint.
    // Closes qa/bugs/2026-05-26-invert-filter-produces-black-image.md and
    // qa/bugs/2026-05-26-asset-file-401-recurrence-likely-ttl.md. Parallel
    // fix to uploadResponseToResultImage; both adapters used to drop these.
    signedUrl: asset.signedUrl,
    signedThumbnailUrl: asset.signedThumbnailUrl,
    width: asset.width ?? 0,
    height: asset.height ?? 0,
    // metadata was widened to Record<string, unknown> in BUG-002 (2026-05-24);
    // narrow before assigning to the string-typed `format` field.
    format: typeof asset.metadata?.format === 'string' ? asset.metadata.format : '',
    sizeBytes: asset.sizeBytes,
  };
}
